import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { Globe, Smartphone, Zap, ShoppingBag, Check } from "lucide-react";
import balansScreen from "../../assets/balans-screenshot.jpg";
import aptacyScreen from "../../assets/aptacy-screenshot.jpg";
import saasScreen   from "../../assets/saas-screenshot.jpg";
import mobileScreen from "../../assets/mobile-screenshot.jpg";
import SectionLabel from "../ui/SectionLabel";
import SplitHeading from "../ui/SplitHeading";
import { useLang } from "../../context/LangContext";
import { c } from "../../content";

const SECTION_BG = {
  strony:    "radial-gradient(ellipse 60% 50% at 20% 50%, rgba(200,121,65,0.05), transparent)",
  aplikacje: "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(124,196,216,0.04), transparent)",
  saas:      "radial-gradient(ellipse 60% 50% at 80% 50%, rgba(28,100,242,0.06), transparent)",
  ecommerce: "radial-gradient(ellipse 60% 50% at 40% 70%, rgba(200,121,65,0.05), transparent)",
};

const CARDS_STATIC = [
  { id: "strony",    num: "01", Icon: Globe,       blobColor: "rgba(200,121,65,0.10)",  iconColor: "#C87941", image: balansScreen },
  { id: "aplikacje", num: "02", Icon: Smartphone,  blobColor: "rgba(124,196,216,0.08)", iconColor: "#7CC4D8", image: mobileScreen },
  { id: "saas",      num: "03", Icon: Zap,         blobColor: "rgba(28,100,242,0.10)",  iconColor: "#6EA3F7", image: saasScreen   },
  { id: "ecommerce", num: "04", Icon: ShoppingBag, blobColor: "rgba(200,121,65,0.09)",  iconColor: "#C87941", image: aptacyScreen },
];

function ServiceCard({ card, onActive }) {
  const { id, num, Icon, blobColor, iconColor, image, title, desc, features } = card;
  const [mousePos, setMousePos]     = useState({ x: 50, y: 50 });
  const [isHovered, setIsHovered]   = useState(false);
  const [sweepPhase, setSweepPhase] = useState("idle");

  return (
    <motion.article
      data-testid={`service-card-${num}`}
      initial={{ borderColor: "rgba(200,121,65,0.12)" }}
      animate={{
        y:           isHovered ? -6 : 0,
        borderColor: isHovered ? "rgba(124,196,216,0.35)" : "rgba(200,121,65,0.12)",
      }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setMousePos({ x: e.clientX - r.left, y: e.clientY - r.top });
      }}
      onMouseEnter={() => { setIsHovered(true); setSweepPhase("running"); onActive(id); }}
      onMouseLeave={() => { setIsHovered(false); onActive(null); }}
      style={{
        position: "relative", overflow: "hidden",
        background: "rgba(255,255,255,0.03)",
        borderWidth: "1px", borderStyle: "solid", borderRadius: 2,
        padding: "clamp(1.25rem, 4vw, 2.5rem)", cursor: "pointer", height: "100%",
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          background: `radial-gradient(circle at ${mousePos.x}px ${mousePos.y}px, ${blobColor} 0%, transparent 55%)`,
          opacity: isHovered ? 1 : 0, transition: "opacity 0.4s ease",
          filter: "blur(2px)", pointerEvents: "none", zIndex: 0,
        }}
      />

      <motion.div
        aria-hidden="true"
        animate={{ x: sweepPhase === "running" ? "150%" : "-100%" }}
        transition={sweepPhase === "running" ? { duration: 0.6, ease: [0.22, 1, 0.36, 1] } : { duration: 0 }}
        onAnimationComplete={() => { if (sweepPhase === "running") setSweepPhase("idle"); }}
        style={{
          position: "absolute", top: 0, left: 0, width: "60%", height: "100%",
          background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)",
          pointerEvents: "none", zIndex: 2,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: "0.7rem", letterSpacing: "0.2em", color: "#7CC4D8", opacity: 0.3, marginBottom: "1.75rem" }}>
          {num}
        </p>

        <Icon
          size={40}
          strokeWidth={1.2}
          style={{
            display: "block",
            color: isHovered ? iconColor : "rgba(242,232,216,0.70)",
            transform: isHovered ? "rotate(8deg) scale(1.1)" : "rotate(0deg) scale(1)",
            transition: "color 0.3s ease, transform 0.3s ease",
            marginBottom: "1.75rem",
          }}
        />

        <h3
          style={{
            fontFamily: '"Cormorant Garamond", Georgia, serif',
            fontSize: "clamp(1.8rem, 2.2vw, 2.2rem)",
            fontWeight: 300,
            color: "#F2E8D8",
            lineHeight: 1.1,
            letterSpacing: isHovered ? "0.05em" : "0.02em",
            transition: "letter-spacing 0.4s ease",
          }}
        >
          {title}
        </h3>

        <p style={{ fontFamily: '"Outfit", sans-serif', fontSize: "0.9rem", color: "rgba(242,232,216,0.60)", lineHeight: 1.7, marginTop: "0.75rem", maxWidth: "36ch" }}>
          {desc}
        </p>

        {features.length > 0 && (
          <ul style={{ marginTop: "1.75rem", display: "flex", flexDirection: "column", gap: "0.45rem", listStyle: "none" }}>
            {features.map((f) => (
              <li key={f} style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontFamily: '"JetBrains Mono", monospace', fontSize: "0.72rem", color: "rgba(242,232,216,0.45)" }}>
                <Check size={11} strokeWidth={2.5} style={{ color: iconColor, opacity: 0.7, flexShrink: 0 }} />
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.article>
  );
}

export default function Services() {
  const { lang } = useLang();
  const [activeCard, setActiveCard] = useState(null);
  const sectionRef = useRef(null);

  const CARDS = CARDS_STATIC.map((s, i) => ({
    ...s,
    title:    c.services.cards[i].title[lang],
    desc:     c.services.cards[i].desc[lang],
    features: c.services.cards[i].features[lang],
  }));

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const yStrony    = useSpring(useTransform(scrollYProgress, [0, 1], [0,  -50]), { stiffness: 60, damping: 20 });
  const yAplikacje = useSpring(useTransform(scrollYProgress, [0, 1], [0,   80]), { stiffness: 60, damping: 20 });
  const ySaas      = useSpring(useTransform(scrollYProgress, [0, 1], [0,  -30]), { stiffness: 60, damping: 20 });
  const yEcommerce = useSpring(useTransform(scrollYProgress, [0, 1], [0,  110]), { stiffness: 60, damping: 20 });

  return (
    <section
      ref={sectionRef}
      id="uslugi"
      className="section-pad bg-navy-deep relative overflow-hidden section-depth"
      style={{ minHeight: "85vh" }}
      data-testid="services-section"
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          background: activeCard ? SECTION_BG[activeCard] : "transparent",
          transition: "background 0.8s ease", pointerEvents: "none", zIndex: 0,
        }}
      />

      <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1 }}>
        <div className="reveal mb-16 lg:mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <SectionLabel>{c.services.label[lang]}</SectionLabel>
            <SplitHeading
              line1={c.services.heading1[lang]}
              line2={<em className="italic text-amber">{c.services.ice[lang]}</em>}
              className="font-display font-light leading-[1.04] mt-5 text-white-warm"
              style={{ fontSize: "clamp(2.4rem, 4.8vw, 4rem)" }}
              data-testid="services-title"
            />
          </div>
          <p className="font-body text-sm text-white-dim max-w-sm leading-[1.8]">
            {c.services.subtitle[lang]}
          </p>
        </div>

        <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start", flexDirection: "column" }}>
          <div style={{ display: "flex", gap: "1.5rem", width: "100%", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", flex: "1 1 300px", minWidth: 0 }}>
              <div className="reveal d1">
                <motion.div style={{ y: yStrony }}>
                  <ServiceCard card={CARDS[0]} onActive={setActiveCard} />
                </motion.div>
              </div>
              <div className="reveal d3">
                <motion.div style={{ y: ySaas }}>
                  <ServiceCard card={CARDS[2]} onActive={setActiveCard} />
                </motion.div>
              </div>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1.5rem", flex: "1 1 300px", minWidth: 0, paddingTop: "3rem" }}
              className="services-right-col"
            >
              <div className="reveal d2">
                <motion.div style={{ y: yAplikacje }}>
                  <ServiceCard card={CARDS[1]} onActive={setActiveCard} />
                </motion.div>
              </div>
              <div className="reveal d4">
                <motion.div style={{ y: yEcommerce }}>
                  <ServiceCard card={CARDS[3]} onActive={setActiveCard} />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
